import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  PlusIcon,
  TrashIcon,
  DocumentIcon,
  LockClosedIcon,
  LockOpenIcon,
  CloudArrowUpIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  PlayCircleIcon,
  VideoCameraIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const CreateCourse = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    courseCode: '',
    credits: 3,
    maxStudents: 30,
    fees: 0,
    category: '',
    level: 'Beginner',
    prerequisites: [''],
    materials: [],
    modules: [],
    thumbnail: ''
  });

  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [expandedModules, setExpandedModules] = useState([]);
  const [showLectureModal, setShowLectureModal] = useState(false);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(null);
  const [lectureFormData, setLectureFormData] = useState({
    title: '',
    duration: '',
    type: 'video',
    videoSource: 'upload',
    videoUrl: '',
    liveType: 'zoom',
    liveLink: '',
    isFreePreview: false
  });

  const categories = [
    'Computer Science', 'Mathematics', 'Physics', 'Chemistry',
    'Biology', 'English', 'History', 'Arts', 'Business', 'Other'
  ];

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handlePrerequisiteChange = (index, value) => {
    const newPrerequisites = [...formData.prerequisites];
    newPrerequisites[index] = value;
    setFormData(prev => ({ ...prev, prerequisites: newPrerequisites }));
  };

  const addPrerequisite = () => {
    setFormData(prev => ({
      ...prev,
      prerequisites: [...prev.prerequisites, '']
    }));
  };

  const removePrerequisite = (index) => {
    const newPrerequisites = formData.prerequisites.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, prerequisites: newPrerequisites }));
  };

  // Material handling functions
  const addMaterial = () => {
    setFormData(prev => ({
      ...prev,
      materials: [...prev.materials, {
        title: '',
        type: 'document',
        description: '',
        isFree: false,
        file: null,
        url: ''
      }]
    }));
  };

  const removeMaterial = (index) => {
    const newMaterials = formData.materials.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, materials: newMaterials }));
  };

  const updateMaterial = (index, field, value) => {
    const newMaterials = [...formData.materials];
    newMaterials[index] = { ...newMaterials[index], [field]: value };
    setFormData(prev => ({ ...prev, materials: newMaterials }));

    // Log the update for debugging
    console.log(`Updated material ${index} field ${field} to:`, value);
    console.log('Full material after update:', newMaterials[index]);
  };

  // Module handling functions
  const addModule = () => {
    const newModule = {
      title: '',
      description: '',
      lectures: []
    };
    setFormData(prev => ({
      ...prev,
      modules: [...prev.modules, newModule]
    }));
    // Auto-expand the new module
    setExpandedModules(prev => [...prev, formData.modules.length]);
  };

  const removeModule = (index) => {
    const newModules = formData.modules.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, modules: newModules }));
    setExpandedModules(prev => prev.filter(i => i !== index).map(i => i > index ? i - 1 : i));
  };

  const updateModule = (index, field, value) => {
    const newModules = [...formData.modules];
    newModules[index] = { ...newModules[index], [field]: value };
    setFormData(prev => ({ ...prev, modules: newModules }));
  };

  const toggleModule = (index) => {
    setExpandedModules(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // Lecture handling functions
  const openLectureModal = (moduleIndex) => {
    setCurrentModuleIndex(moduleIndex);
    setShowLectureModal(true);
  };

  const closeLectureModal = () => {
    setShowLectureModal(false);
    setCurrentModuleIndex(null);
    setLectureFormData({
      title: '',
      duration: '',
      type: 'video',
      videoSource: 'upload',
      videoUrl: '',
      liveType: 'zoom',
      liveLink: '',
      isFreePreview: false
    });
  };

  const handleLectureFormChange = (field, value) => {
    setLectureFormData(prev => ({ ...prev, [field]: value }));
  };

  const addLecture = () => {
    if (!lectureFormData.title.trim()) {
      toast.error('Lecture title is required');
      return;
    }
    if (!lectureFormData.duration.trim()) {
      toast.error('Lecture duration is required');
      return;
    }

    const newLecture = { ...lectureFormData };
    const newModules = [...formData.modules];
    newModules[currentModuleIndex].lectures.push(newLecture);
    setFormData(prev => ({ ...prev, modules: newModules }));
    closeLectureModal();
    toast.success('Lecture added successfully');
  };

  const removeLecture = (moduleIndex, lectureIndex) => {
    const newModules = [...formData.modules];
    newModules[moduleIndex].lectures = newModules[moduleIndex].lectures.filter((_, i) => i !== lectureIndex);
    setFormData(prev => ({ ...prev, modules: newModules }));
  };

  const handleFileUpload = async (index, file) => {
    if (!file) return;

    // Validate file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('File size must be less than 100MB');
      return;
    }

    try {
      setUploadingFiles(prev => [...prev, index]);

      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('type', 'course-material');

      console.log('Uploading file:', file.name, 'for material index:', index);

      const response = await axios.post('/api/upload', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log('Full upload response:', response.data);
      console.log('Response URL field:', response.data.url);
      console.log('Response filePath field:', response.data.filePath);

      // Update material with the uploaded file URL
      const fileUrl = response.data.url || response.data.filePath;
      console.log('Using URL:', fileUrl);

      // Update material with all the file information at once
      const updates = {
        url: fileUrl,
        filename: response.data.filename
      };

      // Auto-fill title if empty
      if (!formData.materials[index].title) {
        updates.title = file.name.split('.')[0];
      }

      // Apply all updates at once
      const newMaterials = [...formData.materials];
      newMaterials[index] = { ...newMaterials[index], ...updates };
      setFormData(prev => ({ ...prev, materials: newMaterials }));

      console.log('Material updated with all data:', newMaterials[index]);

      // Wait a moment and check if the state was updated
      setTimeout(() => {
        console.log('State check after upload - material', index + 1, ':', formData.materials[index]);
        console.log('URL in state:', formData.materials[index]?.url);
      }, 100);

      toast.success('File uploaded successfully');
      console.log('Material should now have URL:', fileUrl);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploadingFiles(prev => prev.filter(i => i !== index));
    }
  };

  const handleThumbnailUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB for images)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingThumbnail(true);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Upload to server
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('type', 'course-thumbnail');

      const response = await axios.post('/api/upload', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const thumbnailUrl = response.data.url || response.data.filePath;
      setFormData(prev => ({ ...prev, thumbnail: thumbnailUrl }));

      toast.success('Thumbnail uploaded successfully');
    } catch (error) {
      console.error('Thumbnail upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload thumbnail');
      setThumbnailPreview(null);
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const removeThumbnail = () => {
    setFormData(prev => ({ ...prev, thumbnail: '' }));
    setThumbnailPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate materials - they must have files uploaded
      for (let i = 0; i < formData.materials.length; i++) {
        const material = formData.materials[i];
        console.log(`Validating material ${i + 1}:`, material);

        if (!material.title.trim()) {
          toast.error(`Material ${i + 1}: Title is required`);
          setLoading(false);
          return;
        }
        if (material.type === 'link') {
          if (!material.url.trim()) {
            toast.error(`Material ${i + 1}: URL is required for link type`);
            setLoading(false);
            return;
          }
        } else {
          // For file materials, upload is required
          if (!material.url) {
            console.log(`Material ${i + 1} missing URL:`, material.url);
            toast.error(`Material ${i + 1}: Please upload a file`);
            setLoading(false);
            return;
          }
          // Check if file is still uploading
          if (uploadingFiles.includes(i)) {
            toast.error(`Material ${i + 1}: File is still uploading, please wait`);
            setLoading(false);
            return;
          }
        }
      }

      // Clean up prerequisites and prepare materials
      const cleanedData = {
        ...formData,
        prerequisites: formData.prerequisites.filter(p => p.trim() !== ''),
        thumbnailImage: formData.thumbnail || '',
        materials: formData.materials.map(material => ({
          title: material.title,
          type: material.type,
          url: material.url,
          filename: material.filename || '',
          description: material.description || '',
          isFree: material.isFree
        })),
        modules: formData.modules.map(module => ({
          title: module.title,
          description: module.description,
          lectures: module.lectures.map(lecture => ({
            title: lecture.title,
            duration: lecture.duration,
            type: lecture.type,
            videoSource: lecture.videoSource,
            videoUrl: lecture.videoUrl,
            liveType: lecture.liveType,
            liveLink: lecture.liveLink,
            isFreePreview: lecture.isFreePreview
          }))
        }))
      };

      console.log("Cleaned Data: ", cleanedData);

      const response = await axios.post('/api/courses', cleanedData);

      toast.success('Course created successfully! Pending admin approval.');

      navigate(`/courses/${response.data.course._id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Create New Course</h1>
        <p className="mt-2 text-gray-600">
          Fill in the details below to create a new course
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Title *
              </label>
              <input
                type="text"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                className="input"
                placeholder="Introduction to Computer Science"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Code *
              </label>
              <input
                type="text"
                name="courseCode"
                required
                value={formData.courseCode}
                onChange={handleChange}
                className="input"
                placeholder="CS101"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                name="category"
                required
                value={formData.category}
                onChange={handleChange}
                className="input"
              >
                <option value="">Select Category</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Level *
              </label>
              <select
                name="level"
                required
                value={formData.level}
                onChange={handleChange}
                className="input"
              >
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Credits *
              </label>
              <input
                type="number"
                name="credits"
                required
                min="1"
                max="10"
                value={formData.credits}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max Students *
              </label>
              <input
                type="number"
                name="maxStudents"
                required
                min="1"
                value={formData.maxStudents}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Fees ($) *
              </label>
              <input
                type="number"
                name="fees"
                required
                min="0"
                value={formData.fees}
                onChange={handleChange}
                className="input"
              />
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              name="description"
              required
              rows={4}
              value={formData.description}
              onChange={handleChange}
              className="input"
              placeholder="Describe what students will learn in this course..."
            />
          </div>

          {/* Course Thumbnail */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Course Thumbnail
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Upload a thumbnail image for your course (recommended: 16:9 aspect ratio, max 5MB)
            </p>

            {thumbnailPreview || formData.thumbnail ? (
              <div className="relative inline-block">
                <img
                  src={thumbnailPreview || formData.thumbnail}
                  alt="Course thumbnail preview"
                  className="w-full max-w-md h-48 object-cover rounded-lg border-2 border-gray-300"
                />
                <button
                  type="button"
                  onClick={removeThumbnail}
                  className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-lg"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors max-w-md">
                <div className="space-y-1 text-center">
                  <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500">
                      <span>Upload thumbnail</span>
                      <input
                        type="file"
                        className="sr-only"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleThumbnailUpload(file);
                          }
                        }}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                  {uploadingThumbnail && (
                    <div className="text-sm text-blue-600">
                      <div className="animate-spin inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2"></div>
                      Uploading...
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Prerequisites */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Prerequisites</h2>
            <button
              type="button"
              onClick={addPrerequisite}
              className="btn btn-secondary btn-sm flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add
            </button>
          </div>

          <div className="space-y-3">
            {formData.prerequisites.map((prereq, index) => (
              <div key={index} className="flex items-center space-x-3">
                <input
                  type="text"
                  value={prereq}
                  onChange={(e) => handlePrerequisiteChange(index, e.target.value)}
                  className="input flex-1"
                  placeholder="Enter prerequisite"
                />
                <button
                  type="button"
                  onClick={() => removePrerequisite(index)}
                  className="btn btn-danger btn-sm"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Course Modules */}
        <div>
          <div className="card h-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Course Modules</h2>
              <button
                type="button"
                onClick={addModule}
                className="btn btn-secondary btn-sm flex items-center"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Module
              </button>
            </div>

            <div className="space-y-4">
              {formData.modules.map((module, moduleIndex) => (
                <div key={moduleIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Module Header */}
                  <div className="bg-gray-50 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3 flex-1">
                        <button
                          type="button"
                          onClick={() => toggleModule(moduleIndex)}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          {expandedModules.includes(moduleIndex) ? (
                            <ChevronUpIcon className="h-5 w-5" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5" />
                          )}
                        </button>
                        <AcademicCapIcon className="h-5 w-5 text-indigo-600" />
                        <span className="font-medium text-gray-900">Module {moduleIndex + 1}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeModule(moduleIndex)}
                        className="btn btn-danger btn-sm"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-8">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Module Title *
                        </label>
                        <input
                          type="text"
                          value={module.title}
                          onChange={(e) => updateModule(moduleIndex, 'title', e.target.value)}
                          className="input text-sm"
                          placeholder="Introduction to the Course"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={module.description}
                          onChange={(e) => updateModule(moduleIndex, 'description', e.target.value)}
                          className="input text-sm"
                          placeholder="Brief description"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Module Content (Lectures) */}
                  {expandedModules.includes(moduleIndex) && (
                    <div className="p-4 bg-white">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-700">Lectures</h4>
                        <button
                          type="button"
                          onClick={() => openLectureModal(moduleIndex)}
                          className="btn btn-primary btn-sm flex items-center"
                        >
                          <PlusIcon className="h-4 w-4 mr-1" />
                          Add Lecture
                        </button>
                      </div>

                      <div className="space-y-2">
                        {module.lectures && module.lectures.length > 0 ? (
                          module.lectures.map((lecture, lectureIndex) => (
                            <div
                              key={lectureIndex}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-center space-x-3">
                                {lecture.type === 'video' ? (
                                  <PlayCircleIcon className="h-5 w-5 text-indigo-600" />
                                ) : (
                                  <VideoCameraIcon className="h-5 w-5 text-green-600" />
                                )}
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{lecture.title}</p>
                                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                                    <span>{lecture.duration}</span>
                                    <span>•</span>
                                    <span>{lecture.type === 'video' ? (
                                      lecture.videoSource === 'upload' ? 'Video Upload' : 'YouTube Link'
                                    ) : (
                                      `Live (${lecture.liveType})`
                                    )}</span>
                                    {lecture.isFreePreview && (
                                      <>
                                        <span>•</span>
                                        <span className="text-green-600 font-medium">Free Preview</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeLecture(moduleIndex, lectureIndex)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-6 text-gray-500">
                            <p className="text-sm">No lectures added yet. Click "Add Lecture" to get started.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {formData.modules.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p>No modules added yet. Click "Add Module" to get started.</p>
                  <p className="text-sm mt-2">Organize your course content into modules and lectures.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Course Materials */}
        <div>
          <div className="card h-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Course Materials</h2>
              <button
                type="button"
                onClick={addMaterial}
                className="btn btn-secondary btn-sm flex items-center"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add
              </button>
            </div>

            <div className="space-y-4">
              {formData.materials.map((material, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-900">Material {index + 1}</h3>
                    <button
                      type="button"
                      onClick={() => removeMaterial(index)}
                      className="btn btn-danger btn-sm"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="space-y-3">
                    {/* Title */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Title *
                      </label>
                      <input
                        type="text"
                        value={material.title}
                        onChange={(e) => updateMaterial(index, 'title', e.target.value)}
                        className="input text-sm"
                        placeholder="Material title"
                        required
                      />
                    </div>

                    {/* Type */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Type *
                      </label>
                      <select
                        value={material.type}
                        onChange={(e) => updateMaterial(index, 'type', e.target.value)}
                        className="input text-sm"
                      >
                        <option value="document">Document/PDF</option>
                        <option value="video">Video</option>
                        <option value="note">Study Notes</option>
                        <option value="link">External Link</option>
                      </select>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={material.description}
                        onChange={(e) => updateMaterial(index, 'description', e.target.value)}
                        className="input text-sm"
                        rows="2"
                        placeholder="Brief description"
                      />
                    </div>

                    {/* File Upload / URL */}
                    {material.type === 'link' ? (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          URL *
                        </label>
                        <input
                          type="url"
                          value={material.url}
                          onChange={(e) => updateMaterial(index, 'url', e.target.value)}
                          className="input text-sm"
                          placeholder="https://example.com"
                          required
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Upload File *
                        </label>
                        <div className="mt-1 flex justify-center px-4 pt-4 pb-4 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                          <div className="space-y-1 text-center">
                            <CloudArrowUpIcon className="mx-auto h-8 w-8 text-gray-400" />
                            <div className="flex text-xs text-gray-600">
                              <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500">
                                <span>Upload</span>
                                <input
                                  type="file"
                                  className="sr-only"
                                  accept={material.type === 'video' ? 'video/*' : '.pdf,.doc,.docx,.txt,.png,.jpg,.jpeg'}
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      updateMaterial(index, 'file', file);
                                      handleFileUpload(index, file);
                                    }
                                  }}
                                />
                              </label>
                            </div>
                            {uploadingFiles.includes(index) && (
                              <div className="text-xs text-blue-600">
                                <div className="animate-spin inline-block w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full mr-1"></div>
                                Uploading...
                              </div>
                            )}
                            {material.url && (
                              <p className="text-xs text-green-600 font-medium">✓ Uploaded</p>
                            )}
                            {!material.url && !uploadingFiles.includes(index) && (
                              <p className="text-xs text-red-500">⚠️ Required</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Access Control */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Access
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name={`material-${index}-access`}
                            checked={!material.isFree}
                            onChange={() => updateMaterial(index, 'isFree', false)}
                            className="h-3 w-3 text-indigo-600"
                          />
                          <span className="ml-2 text-xs text-gray-700 flex items-center">
                            <LockClosedIcon className="h-3 w-3 mr-1" />
                            Premium
                          </span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name={`material-${index}-access`}
                            checked={material.isFree}
                            onChange={() => updateMaterial(index, 'isFree', true)}
                            className="h-3 w-3 text-indigo-600"
                          />
                          <span className="ml-2 text-xs text-gray-700 flex items-center">
                            <LockOpenIcon className="h-3 w-3 mr-1" />
                            Free
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {formData.materials.length === 0 && (
                <div className="text-center py-6 text-gray-500">
                  <DocumentIcon className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                  <p className="text-sm">No materials added yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Lecture Modal */}
        {showLectureModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Add New Lecture</h3>
                <button
                  type="button"
                  onClick={closeLectureModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-4">
                {/* Lecture Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lecture Title *
                  </label>
                  <input
                    type="text"
                    value={lectureFormData.title}
                    onChange={(e) => handleLectureFormChange('title', e.target.value)}
                    className="input"
                    placeholder="Introduction to Variables"
                  />
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (Minutes) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={lectureFormData.duration}
                    onChange={(e) => handleLectureFormChange('duration', e.target.value)}
                    className="input"
                    placeholder="45 minutes"
                  />
                </div>

                {/* Lecture Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lecture Type *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-indigo-500"
                      style={{
                        borderColor: lectureFormData.type === 'video' ? '#4F46E5' : '#E5E7EB',
                        backgroundColor: lectureFormData.type === 'video' ? '#EEF2FF' : 'white'
                      }}
                    >
                      <input
                        type="radio"
                        name="lectureType"
                        value="video"
                        checked={lectureFormData.type === 'video'}
                        onChange={(e) => handleLectureFormChange('type', e.target.value)}
                        className="h-4 w-4 text-indigo-600"
                      />
                      <PlayCircleIcon className="h-5 w-5 ml-3 mr-2 text-indigo-600" />
                      <span className="text-sm font-medium text-gray-900">Video</span>
                    </label>
                    <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all hover:border-green-500"
                      style={{
                        borderColor: lectureFormData.type === 'live' ? '#10B981' : '#E5E7EB',
                        backgroundColor: lectureFormData.type === 'live' ? '#ECFDF5' : 'white'
                      }}
                    >
                      <input
                        type="radio"
                        name="lectureType"
                        value="live"
                        checked={lectureFormData.type === 'live'}
                        onChange={(e) => handleLectureFormChange('type', e.target.value)}
                        className="h-4 w-4 text-green-600"
                      />
                      <VideoCameraIcon className="h-5 w-5 ml-3 mr-2 text-green-600" />
                      <span className="text-sm font-medium text-gray-900">Live Session</span>
                    </label>
                  </div>
                </div>

                {/* Video Options */}
                {lectureFormData.type === 'video' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Video Source *
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:border-indigo-500"
                          style={{
                            borderColor: lectureFormData.videoSource === 'upload' ? '#4F46E5' : '#E5E7EB',
                            backgroundColor: lectureFormData.videoSource === 'upload' ? '#EEF2FF' : 'white'
                          }}
                        >
                          <input
                            type="radio"
                            name="videoSource"
                            value="upload"
                            checked={lectureFormData.videoSource === 'upload'}
                            onChange={(e) => handleLectureFormChange('videoSource', e.target.value)}
                            className="h-4 w-4 text-indigo-600"
                          />
                          <span className="ml-3 text-sm font-medium text-gray-900">Upload Video</span>
                        </label>
                        <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:border-indigo-500"
                          style={{
                            borderColor: lectureFormData.videoSource === 'link' ? '#4F46E5' : '#E5E7EB',
                            backgroundColor: lectureFormData.videoSource === 'link' ? '#EEF2FF' : 'white'
                          }}
                        >
                          <input
                            type="radio"
                            name="videoSource"
                            value="link"
                            checked={lectureFormData.videoSource === 'link'}
                            onChange={(e) => handleLectureFormChange('videoSource', e.target.value)}
                            className="h-4 w-4 text-indigo-600"
                          />
                          <span className="ml-3 text-sm font-medium text-gray-900">YouTube Link</span>
                        </label>
                      </div>
                    </div>

                    {lectureFormData.videoSource === 'upload' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Upload Video File
                        </label>
                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                          <div className="space-y-1 text-center">
                            <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="flex text-sm text-gray-600">
                              <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500">
                                <span>Upload a video file</span>
                                <input
                                  type="file"
                                  className="sr-only"
                                  accept="video/*"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      handleLectureFormChange('videoFile', file);
                                    }
                                  }}
                                />
                              </label>
                              <p className="pl-1">or drag and drop</p>
                            </div>
                            <p className="text-xs text-gray-500">Video files up to 500MB</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {lectureFormData.videoSource === 'link' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          YouTube URL *
                        </label>
                        <input
                          type="url"
                          value={lectureFormData.videoUrl}
                          onChange={(e) => handleLectureFormChange('videoUrl', e.target.value)}
                          className="input"
                          placeholder="https://www.youtube.com/watch?v=..."
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Live Session Options */}
                {lectureFormData.type === 'live' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Platform *
                      </label>
                      <select
                        value={lectureFormData.liveType}
                        onChange={(e) => handleLectureFormChange('liveType', e.target.value)}
                        className="input"
                      >
                        <option value="zoom">Zoom</option>
                        <option value="google-meet">Google Meet</option>
                        <option value="teams">Teams</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Meeting Link *
                      </label>
                      <input
                        type="url"
                        value={lectureFormData.liveLink}
                        onChange={(e) => handleLectureFormChange('liveLink', e.target.value)}
                        className="input"
                        placeholder={lectureFormData.liveType === 'zoom' ? 'https://zoom.us/j/...' : 'https://meet.google.com/...'}
                      />
                    </div>
                  </div>
                )}

                {/* Free Preview Toggle */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1">
                        Free Preview
                      </label>
                      <p className="text-xs text-gray-500">
                        Allow non-enrolled students to watch this lecture
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleLectureFormChange('isFreePreview', !lectureFormData.isFreePreview)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${lectureFormData.isFreePreview ? 'bg-indigo-600' : 'bg-gray-200'
                        }`}
                      role="switch"
                      aria-checked={lectureFormData.isFreePreview}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${lectureFormData.isFreePreview ? 'translate-x-5' : 'translate-x-0'
                          }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeLectureModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={addLecture}
                  className="btn btn-primary"
                >
                  Add Lecture
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/courses')}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Course'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateCourse;
